import {
  Box,
  Button,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Badge,
  Avatar,
  useToast,
  HStack,
  VStack,
  } from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as React from 'react';
import Card from 'components/card/Card';
import CustomSelect from 'components/select/CustomSelect';
import { EditIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FaEye, FaTrash, FaLeaf, FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useGetRemediesQuery, useDeleteRemedyMutation } from 'api/remediesSlice';
import { PageShimmer } from 'components/ui/Shimmer';

const columnHelper = createColumnHelper();

const Remedies = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [sorting, setSorting] = React.useState([]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(15);

  // Chakra color mode values
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const searchBg = useColorModeValue('secondaryGray.300', 'gray.700');
  const searchColor = useColorModeValue('gray.700', 'white');

  // API calls
  const { data: remediesData, isLoading, isError, refetch } = useGetRemediesQuery(
    { 
      search: searchQuery,
      page: currentPage,
      per_page: perPage
    },
    { refetchOnMountOrArgChange: true }
  );
  
  const [deleteRemedy, { isLoading: isDeleting }] = useDeleteRemedyMutation();

  // Transform API data to match table structure
  const remedies = React.useMemo(() => {
    if (!remediesData?.data) return [];
    
    return remediesData.data.map(remedy => ({
      id: remedy.id,
      title: remedy.title,
      disease: remedy.disease,
      diseases: remedy.diseases || [],
      remedyTypes: remedy.remedy_types || [],
      bodySystems: remedy.body_systems || [],
      image: remedy.main_image_url,
      description: remedy.description,
      status: remedy.status,
      visibleToPlan: remedy.visible_to_plan,
      rating: remedy.average_rating,
      reviewCount: remedy.review_count,
      productLink: remedy.product_link,
    }));
  }, [remediesData]);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle page size change
  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle page navigation
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle image click to navigate to product link
  const handleImageClick = (productLink) => {
    if (productLink) {
      window.open(productLink, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        title: 'No product link available',
        description: 'This remedy does not have a product link.',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Debounced search effect
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, currentPage, perPage, refetch]);

  const columns = [
    columnHelper.accessor('image', {
      id: 'image',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Image
        </Text>
      ),
      cell: (info) => (
        <Flex align="center" gap="2">
          <Avatar
            src={info.getValue()}
            size="md"
            borderRadius="lg"
            fallback={<Icon as={FaLeaf} color="green.500" />}
            onClick={() => handleImageClick(info.row.original.productLink)}
            cursor="pointer"
            _hover={{ opacity: 0.8 }}
            transition="opacity 0.2s"
          />
                     {info.row.original.productLink && (
             <Icon
               as={FaArrowUpRightFromSquare}
               color="brand.500"
               w="12px"
               h="12px"
               cursor="pointer"
               onClick={() => handleImageClick(info.row.original.productLink)}
               _hover={{ color: "blue.600" }}
             />
           )}
        </Flex>
      ),
    }),
    columnHelper.accessor('title', {
      id: 'title',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Title
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontWeight="bold">
          {info.getValue()}
        </Text>
      ),
    }),



    columnHelper.accessor('visibleToPlan', {
      id: 'visibleToPlan',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Visibility
        </Text>
      ),
             cell: (info) => {
         const getPlanColor = (plan) => {
           switch (plan) {
             case 'all':
               return 'blue';
             case 'rookie':
               return 'green';
             case 'skilled':
               return 'orange';
             case 'master':
               return 'purple';
             default:
               return 'gray';
           }
         };
         
         return (
           <Badge 
             colorScheme={getPlanColor(info.getValue())}
             px="2"
             py="1"
             borderRadius="full"
             fontSize="xs"
             textTransform="capitalize"
           >
             {info.getValue()}
           </Badge>
         );
       },
    }),
    columnHelper.accessor('status', {
      id: 'status',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Status
        </Text>
      ),
      cell: (info) => (
        <Badge 
          colorScheme={info.getValue() === 'active' ? 'green' : 'red'}
          px="2"
          py="1"
          borderRadius="full"
          fontSize="xs"
          textTransform="capitalize"
        >
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
        >
          Actions
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Icon
            w="18px"
            h="18px"
            me="10px"
            color="red.500"
            as={FaTrash}
            cursor={isDeleting ? "not-allowed" : "pointer"}
            opacity={isDeleting ? 0.5 : 1}
            onClick={isDeleting ? undefined : () => handleDeleteRemedy(info.getValue())}
          />
          <Icon
            w="18px"
            h="18px"
            me="10px"
            color="green.500"
            as={EditIcon}
            cursor="pointer"
            onClick={() => navigate(`/admin/edit-remedy/${info.getValue()}`)}
          />
          <Icon
            w="18px"
            h="18px"
            me="10px"
            color="brand.500"
            as={FaEye}
            cursor="pointer"
            onClick={() => navigate(`/admin/remedy/${info.getValue()}`)}
          /> 
        </Flex>
      ),
    }),
  ];

  const table = useReactTable({
    data: remedies,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  // Delete function
  const handleDeleteRemedy = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      });

      if (result.isConfirmed) {
        await deleteRemedy(id).unwrap();
        
        toast({
          title: 'Remedy deleted',
          description: 'The remedy has been successfully deleted.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        refetch();
      }
    } catch (error) {
      console.error('Failed to delete remedy:', error);
      
      let errorMessage = 'Failed to delete the remedy';
      if (error?.data?.message) {
        errorMessage = error.data.message;
      }
      
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box className="nr-page">
        <PageShimmer variant="table" />
      </Box>
    );
  }

  // Error state
  if (isError) {
    return (
      <Box className="nr-page">
        <Card
          flexDirection="column"
          w="100%"
          px="0px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <Flex justify="center" align="center" h="200px">
            <Text color="red.500">Error loading remedies. Please try again.</Text>
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="nr-page">
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex
          px={{ base: "16px", md: "25px" }}
          mb="8px"
          direction={{ base: "column", md: "row" }}
          justifyContent="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={{ base: 4, md: 0 }}
        >
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Remedies
          </Text>

          <Box w={{ base: "100%", md: "auto" }}>
            <InputGroup w={{ base: "100%", md: "400px" }}>
              <InputLeftElement>
                <IconButton
                  bg="inherit"
                  borderRadius="inherit"
                  _hover="none"
                  _active={{
                    bg: "inherit",
                    transform: "none",
                    borderColor: "transparent",
                  }}
                  _focus={{
                    boxShadow: "none",
                  }}
                  icon={<SearchIcon w="15px" h="15px" />}
                />
              </InputLeftElement>
              <Input
                variant="search"
                fontSize="sm"
                bg={searchBg}
                color={searchColor}
                fontWeight="500"
                _placeholder={{ color: "gray.400", fontSize: "14px" }}
                borderRadius="30px"
                placeholder="Search remedies..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </InputGroup>
          </Box>

          <Button
            variant="darkBrand"
            color="white"
            fontSize="sm"
            fontWeight="500"
            borderRadius="70px"
            px="24px"
            py="5px"
            onClick={() => navigate('/admin/add-remedy')}
            w={{ base: "100%", md: "200px" }}
          >
            Add New Remedy
          </Button>
        </Flex>

        <Box>
          <Table variant="simple" color="gray.500" mb="24px" mt="12px">
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          justifyContent="space-between"
                          align="center"
                          fontSize={{ sm: '10px', lg: '12px' }}
                          color="gray.400"
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          {{
                            asc: ' 🔼',
                            desc: ' 🔽',
                          }[header.column.getIsSorted()] ?? null}
                        </Flex>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody>
              {table.getRowModel().rows.map((row) => {
                return (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Td
                          key={cell.id}
                          fontSize={{ sm: '14px' }}
                          minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                          borderColor="transparent"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination Controls */}
        {remediesData?.pagination && (
          <Flex
            px={{ base: "16px", md: "25px" }}
            py="20px"
            justify="space-between"
            align="center"
            borderTop="1px solid"
            borderColor={borderColor}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
          >
            {/* Page Info */}
            <Text color={textColor} fontSize="sm">
              Showing {remediesData.pagination.from} to {remediesData.pagination.to} of {remediesData.pagination.total} results
            </Text>

            {/* Pagination Controls */}
            <HStack spacing="3">
              {/* Page Size Selector */}
              <HStack spacing="2">
                <Text fontSize="sm" color={textColor}>
                  Show:
                </Text>
                <CustomSelect
                  size="sm"
                  value={perPage}
                  onChange={handlePerPageChange}
                  w="70px"
                  bg={searchBg}
                  color={searchColor}
                >
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </CustomSelect>
              </HStack>

              {/* Page Navigation */}
              <HStack spacing="2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage - 1)}
                  isDisabled={currentPage <= 1}
                  leftIcon={<ChevronLeftIcon />}
                >
                  Previous
                </Button>

                {/* Page Numbers */}
                {Array.from({ length: Math.min(5, remediesData.pagination.last_page) }, (_, i) => {
                  const pageNum = i + 1;
                  const isCurrentPage = pageNum === currentPage;
                  
                  return (
                    <Button
                      key={pageNum}
                      size="sm"
                      variant={isCurrentPage ? "solid" : "outline"}
                      colorScheme={isCurrentPage ? "brand" : "gray"}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handlePageChange(currentPage + 1)}
                  isDisabled={currentPage >= remediesData.pagination.last_page}
                  rightIcon={<ChevronRightIcon />}
                >
                  Next
                </Button>
              </HStack>
            </HStack>
          </Flex>
        )}
      </Card>
    </Box>
  );
};

export default Remedies;